import {
  emitTransportEvent,
  type PlayerIdentity,
  type SignalingTransport,
} from "./SignalingTransport";

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: number;
}

interface ServerMessage {
  type: "ready" | "response" | "event";
  id?: string;
  event?: string;
  ok?: boolean;
  data?: unknown;
  error?: string;
  iceSessionToken?: string;
}

const CONNECTION_TIMEOUT_MS = 8_000;
const REQUEST_TIMEOUT_MS = 10_000;

export class WebSocketTransport extends EventTarget implements SignalingTransport {
  private socket: WebSocket | null = null;
  private ready = false;
  private intentionalClose = false;
  private iceSessionToken: string | null = null;
  private sequence = 0;
  private readonly pending = new Map<string, PendingRequest>();

  constructor(private readonly url: string) {
    super();
  }

  get connected(): boolean {
    return this.ready && this.socket?.readyState === WebSocket.OPEN;
  }

  get sessionToken(): string | null {
    return this.iceSessionToken;
  }

  connect(identity: PlayerIdentity): Promise<void> {
    if (this.connected) return Promise.resolve();
    this.intentionalClose = false;
    this.iceSessionToken = null;
    this.socket?.close();
    const socket = new WebSocket(this.url);
    this.socket = socket;
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        socket.close();
        reject(new Error("The signaling service did not answer."));
      }, CONNECTION_TIMEOUT_MS);
      const finish = (callback: () => void): void => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        callback();
      };
      socket.addEventListener("open", () => this.send({ type: "hello", identity }));
      socket.addEventListener("message", (event) => {
        const message = parseMessage(event.data);
        if (message?.type === "ready") finish(() => {
          this.ready = true;
          this.iceSessionToken = typeof message.iceSessionToken === "string" ? message.iceSessionToken : null;
          resolve();
        });
        this.acceptMessage(message);
      });
      socket.addEventListener("error", () => finish(() => reject(new Error("The signaling service could not be reached."))));
      socket.addEventListener("close", () => {
        const wasReady = this.ready;
        this.ready = false;
        finish(() => reject(new Error("The signaling connection closed before it was ready.")));
        this.rejectPending(new Error("The signaling connection closed."));
        if (wasReady && !this.intentionalClose) emitTransportEvent(this, "offline", null);
      });
    });
  }

  setIdentity(identity: PlayerIdentity): void {
    if (this.connected) this.notify("identity:update", identity);
  }

  request<T>(event: string, data: object): Promise<T> {
    if (!this.connected) return Promise.reject(new Error("The signaling connection is not ready."));
    const id = `${Date.now().toString(36)}-${(++this.sequence).toString(36)}`;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error("The signaling request timed out."));
      }, REQUEST_TIMEOUT_MS);
      this.pending.set(id, { resolve: (value) => resolve(value as T), reject, timer });
      this.send({ type: "request", id, event, data });
    });
  }

  notify(event: string, data: object): void {
    if (this.connected) this.send({ type: "event", event, data });
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.ready = false;
    this.iceSessionToken = null;
    this.socket?.close(1000, "Client closed");
    this.socket = null;
    this.rejectPending(new Error("The signaling connection closed."));
  }

  private acceptMessage(message: ServerMessage | null): void {
    if (!message) return;
    if (message.type === "event" && message.event) {
      emitTransportEvent(this, message.event, message.data);
      return;
    }
    if (message.type !== "response" || !message.id) return;
    const pending = this.pending.get(message.id);
    if (!pending) return;
    window.clearTimeout(pending.timer);
    this.pending.delete(message.id);
    if (message.ok) pending.resolve(message.data);
    else pending.reject(new Error(message.error || "The signaling request failed."));
  }

  private send(message: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  private rejectPending(error: Error): void {
    for (const request of this.pending.values()) {
      window.clearTimeout(request.timer);
      request.reject(error);
    }
    this.pending.clear();
  }
}

const parseMessage = (value: unknown): ServerMessage | null => {
  if (typeof value !== "string") return null;
  try {
    const message = JSON.parse(value) as ServerMessage;
    return message && typeof message.type === "string" ? message : null;
  } catch {
    return null;
  }
};
